import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PagedData } from '@app/modules/lookup/models/paged-data.model';
import { DocumentCreateRequest, DocumentItem, DocumentStatus, DocumentUpdateRequest, FileUploadRequest, FolderCreateRequest } from '../models/document.model';
import { EnvironmentService } from "@app/core/services/environment.service";
import { api_routes } from '@app/consts';

const headers = new HttpHeaders({
    'Content-Type': 'application/json',
});

@Injectable({
    providedIn: 'root'
})
export class DocumentsService {

    constructor(
        private http: HttpClient,
        private envService: EnvironmentService
    ) { }

    getFolderContents(take?: number, skip?: number, filter?: string, sort?: string, asOf?: string): Observable<PagedData<DocumentItem>> {
        let params = new HttpParams();
        sort ? params = params.append('sort', sort) : null;
        filter ? params = params.append('filter', filter) : null;
        asOf ? params = params.append('asOf', asOf) : null;
        skip ? params = params.append('skip', skip) : null;
        take ? params = params.append('take', take) : null;

        return this.http.get<any>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}`, {
            headers: headers,
            params: params
        });
    }

    createFolder(createRequest: FolderCreateRequest): Observable<DocumentItem> {
        return this.http.post<DocumentItem>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/Folder`, createRequest, {
            headers: headers
        })
    }

    updateFolder(id: string, updateRequest: DocumentUpdateRequest): Observable<DocumentItem> {
        return this.http.put<DocumentItem>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/Folder/${id}`, updateRequest, {
            headers: headers
        })
    }

    createFile(fileUploadRequest: FileUploadRequest): Observable<DocumentItem> {
        const formData = new FormData();

        if (fileUploadRequest.file) {
            formData.append('file', fileUploadRequest.file);
        }
        formData.append('comments', fileUploadRequest.comments || '');
        formData.append('inFolder', fileUploadRequest.inFolder || '');
        formData.append('isPrivate', (fileUploadRequest.isPrivate || false).toString());
        formData.append('status', (fileUploadRequest.status || 'DRAFT'));

        if (fileUploadRequest.workLocations && fileUploadRequest.workLocations.length > 0) {
            fileUploadRequest.workLocations.forEach((id, index) => {
                formData.append(`workLocations[${index}]`, id);
            });
        }

        if (fileUploadRequest.roles && fileUploadRequest.roles.length > 0) {
            fileUploadRequest.roles.forEach((id, index) => {
                formData.append(`roles[${index}]`, id);
            });
        }

        return this.http.post<DocumentItem>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/File`, formData, {
            observe: 'response',
        })
            .pipe(
                catchError(err => throwError(err)),
                map(res => res.body!)
            );
    }

    updateFile(id: string, updateRequest: DocumentUpdateRequest): Observable<DocumentItem> {
        return this.http.put<DocumentItem>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/File/${id}`, updateRequest, {
            headers: headers,
        })
    }

    getFile(id: string): Observable<DocumentItem> {
        return this.http.get<DocumentItem>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/File/${id}`, {
            headers: headers
        })
    }
    
    getItem(id: string): Observable<DocumentItem> {
        return this.http.get<DocumentItem>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/${id}`, {
            headers: headers
        })
    }

    deleteItem(id: string): Observable<void> {
        return this.http.delete<void>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/Delete/${id}`, {
            headers: headers,
            observe: 'response',
        })
            .pipe(
                catchError(err => throwError(err)),
                map(res => res.body!)
            );
    }

    downloadFile(fileId: string): Observable<Blob> {
        return this.http.get(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/${fileId}/${api_routes.DOWNLOAD}`, {
            responseType: 'blob',
            observe: 'response',
        })
            .pipe(
                catchError(err => throwError(err)),
                map(res => res.body!)
            );
    }

    getDocumentStatuses(take: number, skip: number): Observable<PagedData<DocumentStatus>> {
        let params = new HttpParams();
        skip ? params = params.append('skip', skip) : null;
        take ? params = params.append('take', take) : null;

        return this.http.get<PagedData<DocumentStatus>>(`${this.envService.env.apiUrl}${api_routes.DOCUMENTS_LIBRARY}/Statuses`, {
            headers: headers,
            params: params
        })
    }
}
